import React from "react";
import {createUseStyles} from "react-jss";
import {Colors} from "../../constants/colors";
import {ObservabilityMetricPoint, ObservabilityMetricState, ObservabilityPeriod} from "../../types/observability";
import {NumericTimeSeries} from "../../redux/types";
import Card from "../Card";
import MetricChart from "./MetricChart";
import {NumericMetric} from "./Metrics";

interface Props {
  title: string
  subTitle: string
  metric: ObservabilityMetricState
  period: ObservabilityPeriod
  standalone?: boolean
}

const periodCaptions: Record<ObservabilityPeriod, string> = {
  day: 'Last 24h',
  week: 'Last 7 days',
  month: 'Last 30 days'
}

const ObservabilityMetricCard = ({
  title,
  subTitle,
  metric,
  period,
  standalone
}: Props) => {
  const styles = useStyles()
  const selectedPeriodLabel = periodCaptions[period]
  const points = metric.series?.points ?? []
  const chartMetric = toNumericTimeSeries(points)

  return (
    <Card className={[
      styles.card,
      standalone ? styles.standalone : undefined
    ].filter(Boolean).join(' ')}>
      <div className={styles.cardContent}>
        <div className={standalone ? styles.standaloneTitle : styles.title}>{title}</div>
        <div className={styles.subTitle}>{subTitle}</div>
        <div className={styles.body}>
          <div className={metric.loading ? styles.metricLoading : undefined}>
            <NumericMetric
              value={metric.total}
              caption={selectedPeriodLabel}
              size={40}
            />
          </div>
          {metric.loading && <div className={styles.loadingIndicator}>Updating...</div>}
          {metric.error && <div className={styles.error}>Failed to load metric</div>}
        </div>
      </div>
      {chartMetric &&
          <div className={styles.chartContainer}>
              <MetricChart label={title} metric={chartMetric} from={metric.from} to={metric.to}/>
          </div>
      }
    </Card>
  )
}

const toNumericTimeSeries = (points: ObservabilityMetricPoint[]): NumericTimeSeries | null => {
  if (points.length === 0) {
    return null
  }

  const values = points.map(point => ({
    date: point.timestamp,
    value: point.value
  }))
  const numericValues = values.map(point => point.value)
  const firstValue = numericValues[0] ?? 0
  const lastValue = numericValues[numericValues.length - 1] ?? 0
  const delta = lastValue - firstValue
  const average = numericValues.reduce((sum, value) => sum + value, 0) / numericValues.length

  return {
    values,
    firstValue,
    lastValue,
    min: Math.min(...numericValues),
    max: Math.max(...numericValues),
    delta,
    average,
    deltaPercentage: firstValue === 0 ? 0 : delta / firstValue * 100,
    currentPercentage: average === 0 ? 0 : lastValue / average * 100
  }
}

export default ObservabilityMetricCard

const useStyles = createUseStyles({
  card: {
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: '#4B4B4B',
    display: 'flex',
    flexDirection: 'column',
    width: 350,
    height: 250,
    flex: '0 0 auto'
  },
  standalone: {
    backgroundColor: Colors.darkGrayCard,
    borderColor: Colors.darkGrayBorder,
  },
  cardContent: {
    pointerEvents: 'none',
    display: 'flex',
    flexDirection: 'column',
    rowGap: 10,
    padding: '20px 25px 12px 25px',
    flex: '0 0 auto'
  },
  chartContainer: {
    display: 'flex',
    flex: 1,
    width: '100%',
    minHeight: 0
  },
  title: {
    fontSize: 13,
    textTransform: 'uppercase'
  },
  standaloneTitle: {
    color: Colors.euro_600,
    fontSize: 15,
    textTransform: 'uppercase'
  },
  subTitle: {
    fontSize: 12,
    color: Colors.gray3
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: 10,
  },
  metricLoading: {
    transition: 'opacity 0.18s ease-in-out',
    opacity: 0.55
  },
  loadingIndicator: {
    color: Colors.gray3,
    fontSize: 12,
    opacity: 0.72
  },
  error: {
    color: Colors.rubel_600,
    fontSize: 12
  }
})
